/**
 * Lustro stroju / posiadanych itemów w SaveLocal (§12).
 * Serwer = autorytet; lokalnie cache do offline preview i optimistic UI.
 */
import { SaveLocal } from '../core/SaveLocal.js';
import { scopedKey } from '../core/profileScope.js';
import { balanceBus } from '../core/balanceBus.js';
import {
  emptyOutfitSlots,
  emptySwimSlots,
  normalizeOutfitSlots,
  normalizeSwimSlots,
  type OutfitSlots,
  type SwimSlots,
} from './parseOutfit.js';

const KEY_SLOTS = 'pieskowo.outfit.slots';
const KEY_SWIM = 'pieskowo.outfit.swimSlots';
const KEY_VERSION = 'pieskowo.outfit.version';
const KEY_OWNED = 'pieskowo.ownedItems';
const KEY_BALANCE = 'pieskowo.balanceZl';
const KEY_BREED = 'pieskowo.dog.breed';
const KEY_COLOR = 'pieskowo.dog.color';
const KEY_DOG_NAME = 'pieskowo.dog.name';

export type OutfitCache = {
  slots: OutfitSlots;
  swimSlots: SwimSlots;
  version: number;
  ownedItems: string[];
  balanceZl: number;
  breed: string;
  color: string;
  dogName: string;
};

export const OutfitStore = {
  load(): OutfitCache {
    const slotsRaw = SaveLocal.getJson<unknown>(scopedKey(KEY_SLOTS), null);
    const swimRaw = SaveLocal.getJson<unknown>(scopedKey(KEY_SWIM), null);
    return {
      slots: slotsRaw
        ? normalizeOutfitSlots(slotsRaw as Record<string, unknown>)
        : emptyOutfitSlots(),
      swimSlots: swimRaw
        ? normalizeSwimSlots(swimRaw as Record<string, unknown>)
        : emptySwimSlots(),
      version: SaveLocal.getJson<number>(scopedKey(KEY_VERSION), 0) || 0,
      ownedItems: SaveLocal.getJson<string[]>(scopedKey(KEY_OWNED), []),
      balanceZl: SaveLocal.getJson<number>(scopedKey(KEY_BALANCE), 0) || 0,
      breed: SaveLocal.getJson<string>(scopedKey(KEY_BREED), 'maltese') || 'maltese',
      color: SaveLocal.getJson<string>(scopedKey(KEY_COLOR), '#FFF5EE') || '#FFF5EE',
      dogName: SaveLocal.getJson<string>(scopedKey(KEY_DOG_NAME), 'Misia') || 'Misia',
    };
  },

  saveSlots(slots: OutfitSlots): void {
    SaveLocal.setJson(scopedKey(KEY_SLOTS), slots);
  },

  saveSwimSlots(swim: SwimSlots): void {
    SaveLocal.setJson(scopedKey(KEY_SWIM), swim);
  },

  saveVersion(version: number): void {
    SaveLocal.setJson(scopedKey(KEY_VERSION), version);
  },

  saveOwned(owned: string[]): void {
    SaveLocal.setJson(scopedKey(KEY_OWNED), owned);
  },

  saveBalance(balanceZl: number): void {
    SaveLocal.setJson(scopedKey(KEY_BALANCE), balanceZl);
    // HUD live: zakupy / odczyty salda idą tą samą drogą
    balanceBus.set(balanceZl);
  },

  saveDog(meta: { breed?: string; color?: string; dogName?: string }): void {
    if (meta.breed) SaveLocal.setJson(scopedKey(KEY_BREED), meta.breed);
    if (meta.color) SaveLocal.setJson(scopedKey(KEY_COLOR), meta.color);
    if (meta.dogName) SaveLocal.setJson(scopedKey(KEY_DOG_NAME), meta.dogName);
  },

  /** Po udanym PUT outfit. */
  commitOutfit(slots: OutfitSlots, swim: SwimSlots, version: number): void {
    this.saveSlots(slots);
    this.saveSwimSlots(swim);
    this.saveVersion(version);
  },

  /** Po udanym purchase. */
  commitPurchase(balance: number, ownedItems?: string[]): void {
    this.saveBalance(balance);
    if (ownedItems) this.saveOwned(ownedItems);
  },
};
